import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import { ConfirmationModal } from './ConfirmationModal';
import { Dropdown } from './Dropdown';
import { MultiSelectDropdown } from './MultiSelectDropdown';
import { Table } from './Table';
import { TextInput } from './TextInput';
import { Button } from './buttons/Button';
import { axios, updatePet, updatePetAndPetPlan } from '../api';
import { Close } from '../assets/Close';
import { DownArrow } from '../assets/DownArrow';
import { InlineSpinner } from '../assets/Spinner';
import { booleanOptions, monthOptions, yearOptions, genderOptions } from '../dropdownOptions';
import { Customer, Option } from '../pages/types';
import { BOTTOM_TOAST, getCountryFlag, generateErrorMsg } from '../utils';
import { buildBuildPlanPayload } from '../utils/calculatorUtils';

enum UpdatePetStepper {
  InputData,
  Review,
}

interface UpdatePetModalProps {
  customer: Customer;
  petId: string;
  onConfirm: () => void;
  onCancel: () => void;
  lifestyleOptions: any;
  bodyTypeOptions: any;
  breedOptions: Option[];
  healthConditionOptions: Option[];
}

export const UpdatePetModal = ({
  customer,
  petId,
  onConfirm,
  onCancel,
  lifestyleOptions,
  bodyTypeOptions,
  breedOptions,
  healthConditionOptions,
}: UpdatePetModalProps) => {
  const pet = useMemo(() => {
    return customer.pets.find((pet) => pet.id === petId);
  }, [customer.pets, petId]);
  const petplan = useMemo(() => {
    return customer.petplans.find((petplan: any) => petplan.pet === petId);
  }, [customer.petplans, petId]);
  const mealPlanProduct = useMemo(() => {
    return petplan?.products.find((product: any) => product.product_type === 'MEAL');
  }, [petplan?.products]);
  const previousWeight = pet?.properties?.weight;
  const previousNeuteredOrSpayedOption =
    booleanOptions.find((option) => option.value === pet?.properties?.neutered?.toString()) ??
    booleanOptions[0];
  const previousMonthOption = monthOptions.find(
    (option) => option.value === pet?.birth_month?.toString()
  );
  const previousYearOption =
    yearOptions.find((option) => option.value === pet?.birth_year?.toString()) ?? yearOptions[0];
  const previousLifestyleOption =
    lifestyleOptions.find(
      (option: any) => option.value === pet?.properties?.lifestyle?.toString()
    ) ?? lifestyleOptions[1];
  const previousBodyTypeOption =
    bodyTypeOptions.find(
      (option: any) => option.value === pet?.properties?.body_type?.toString()
    ) ?? bodyTypeOptions[1];
  const previousGenderOption =
    genderOptions.find((option) => option.value === pet?.gender?.toString()) ?? genderOptions[0];
  const previousHealthConditions = healthConditionOptions.filter((condition) =>
    pet?.properties?.health_conditions?.includes(condition.value)
  );
  const previousBreedOptions = breedOptions.filter((breed) => pet?.breeds?.includes(breed.value));
  const dogNamePattern = /^[A-Za-zÀ-ÖØ-öø-ÿ.' -]*$/;
  const [step, setStep] = useState<UpdatePetStepper>(UpdatePetStepper.InputData);
  const [dogName, setDogName] = useState(pet?.name);
  const [isNeuteredOrSpayed, setIsNeuteredOrSpayed] = useState(previousNeuteredOrSpayedOption);
  const [selectedMonth, setSelectedMonth] = useState(previousMonthOption);
  const [selectedYear, setSelectedYear] = useState(previousYearOption);
  const [selectedBodyType, setSelectedBodyType] = useState(previousBodyTypeOption);
  const [weight, setWeight] = useState(previousWeight);
  const [selectedLifestyle, setSelectedLifestyle] = useState(previousLifestyleOption);
  const [isLoading, setIsLoading] = useState(false);
  const [recommendedMealPlan, setRecommendedMealPlan] = useState<any | null>(null);
  const [showCurrentPlanConfirmModal, setShowCurrentPlanConfirmModal] = useState(false);
  const [showRecommendedPlanConfirmModal, setShowRecommendedPlanConfirmModal] = useState(false);
  const [selectedBreeds, setSelectedBreeds] = useState<Option[]>(previousBreedOptions);
  const [selectedGender, setSelectedGender] = useState(previousGenderOption);
  const [selectedHealthConditions, setSelectedHealthConditions] =
    useState<Option[]>(previousHealthConditions);
  const possessiveAdj = pet?.gender === 'MALE' ? 'his' : 'her';

  const onKeepCurrentPlan = () => {
    updatePet({
      petId,
      dogName,
      weight,
      customerId: customer.id,
      isNeuteredOrSpayed: isNeuteredOrSpayed.value,
      recommended_calories_day: recommendedMealPlan.plan_size,
      selectedMonth: selectedMonth?.value,
      selectedYear: selectedYear.value,
      selectedBodyType: selectedBodyType.value,
      selectedLifestyle: selectedLifestyle.value,
      selectedHealthConditions: selectedHealthConditions.map((condition) => condition.value),
      breeds: selectedBreeds.map((breed) => breed.value),
      gender: selectedGender.value,
    });
    onConfirm();
  };

  const onUpdateToRecommendedPlan = () => {
    updatePetAndPetPlan({
      petId,
      dogName,
      weight,
      customerId: customer.id,
      isNeuteredOrSpayed: isNeuteredOrSpayed.value,
      recommended_calories_day: recommendedMealPlan.plan_size,
      selectedMonth: selectedMonth?.value,
      selectedYear: selectedYear.value,
      selectedBodyType: selectedBodyType.value,
      selectedLifestyle: selectedLifestyle.value,
      selectedHealthConditions: selectedHealthConditions.map((condition) => condition.value),
      productCode: recommendedMealPlan?.subscription?.product?.code,
      price: recommendedMealPlan?.subscription?.price?.price,
      frequency: recommendedMealPlan?.subscription?.product?.frequency,
      petPlanId: petplan?.id,
      mealPlanProductId: mealPlanProduct?.id,
      breeds: selectedBreeds.map((breed) => breed.value),
      gender: selectedGender.value,
    });
    onConfirm();
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 modal">
      <div className="bg-white rounded-lg shadow-md p-4 min-w-[550px] relative border">
        <Close className="absolute top-1 right-1 w-8 h-8 cursor-pointer" onClick={onCancel} />
        {step === UpdatePetStepper.Review && (
          <>
            <div className="absolute top-1 left-2">
              <h3
                className="flex text-lg font-semibold text-left items-center cursor-pointer"
                onClick={() => setStep(UpdatePetStepper.InputData)}
              >
                <div className="w-[20px] h-[28px] overflow-hidden relative">
                  <DownArrow className="absolute left-[-15px] top-[-2px] w-[40px] h-[40px] rotate-90" />
                </div>
                Back
              </h3>
            </div>
            <div className="w-1 h-3" />
          </>
        )}
        <h3 className="text-lg font-semibold text-left">Update Dog</h3>
        <p className="text-sm mb-2 text-left">
          <b>Customer</b>:{' '}
          {`${getCountryFlag(customer.default_country)} ${customer.first_name} ${
            customer.last_name
          } (${customer.email})`}
        </p>
        <hr />
        {step === UpdatePetStepper.InputData && (
          <div className="flex justify-left flex-col items-left">
            <div className="text-md font-semibold text-left">
              <h3>Dog name</h3>
            </div>
            <div className="w-full mb-2">
              <TextInput
                value={dogName}
                placeholder="Enter Dog's Name"
                setTextChange={setDogName}
              />
            </div>

            <div className="flex">
              <div className="w-1/2 pr-2">
                <div className="text-md font-semibold text-left">
                  <h3>Gender</h3>
                </div>
                <div className="w-full mb-2">
                  <Dropdown
                    options={genderOptions}
                    setSelectedOption={(newSelection) => setSelectedGender(newSelection)}
                    selectedOption={selectedGender}
                  />
                </div>
              </div>

              <div className="w-1/2 pl-2">
                <div className="text-md font-semibold text-left">
                  <h3>Neutered / Spayed</h3>
                </div>
                <div className="w-full mb-2">
                  <Dropdown
                    options={booleanOptions}
                    setSelectedOption={(newSelection) => setIsNeuteredOrSpayed(newSelection)}
                    selectedOption={isNeuteredOrSpayed}
                  />
                </div>
              </div>
            </div>

            <div className="text-md font-semibold text-left">
              <h3>Breed</h3>
            </div>
            <div className="w-[515px] mb-2">
              <MultiSelectDropdown
                options={breedOptions}
                placeholder="Select breed"
                selectedOptions={selectedBreeds}
                setSelectedOptions={setSelectedBreeds}
              />
            </div>

            <div className="flex">
              {/* Birth Month */}
              <div className="w-1/2 pr-2">
                <div className="text-md font-semibold text-left">
                  <h3>Birth Month</h3>
                </div>
                <div className="w-full mb-2">
                  <Dropdown
                    options={monthOptions}
                    setSelectedOption={(newSelection) => setSelectedMonth(newSelection)}
                    selectedOption={selectedMonth}
                  />
                </div>
              </div>

              {/* Birth Year */}
              <div className="w-1/2 pl-2">
                <div className="text-md font-semibold text-left">
                  <h3>Birth Year</h3>
                </div>
                <div className="w-full mb-2">
                  <Dropdown
                    options={yearOptions}
                    setSelectedOption={(newSelection) => setSelectedYear(newSelection)}
                    selectedOption={selectedYear}
                  />
                </div>
              </div>
            </div>

            <div className="text-md font-semibold text-left">
              <h3>Health Conditions</h3>
            </div>
            <div className="w-[515px] mb-2">
              <MultiSelectDropdown
                options={healthConditionOptions}
                selectedOptions={selectedHealthConditions}
                setSelectedOptions={setSelectedHealthConditions}
              />
            </div>

            <div className="flex">
              <div className="w-1/2 pr-2">
                <div className="text-md font-semibold text-left">
                  <h3>Body Type</h3>
                </div>
                <div className="w-full mb-2">
                  <Dropdown
                    options={bodyTypeOptions}
                    setSelectedOption={(newSelection) => setSelectedBodyType(newSelection)}
                    selectedOption={selectedBodyType}
                  />
                </div>
              </div>

              <div className="w-1/2 pl-2">
                <div className="text-md font-semibold text-left">
                  <h3>Lifestyle</h3>
                </div>
                <div className="w-full mb-2">
                  <Dropdown
                    options={lifestyleOptions}
                    setSelectedOption={(newSelection) => setSelectedLifestyle(newSelection)}
                    selectedOption={selectedLifestyle}
                  />
                </div>
              </div>
            </div>

            <div className="text-md font-semibold text-left">
              <h3>Weight</h3>
            </div>
            <div className="w-1/2 mb-2 flex">
              <TextInput
                value={weight ? weight.toString() : ''}
                placeholder="Enter Weight"
                setTextChange={(newValue) => setWeight(parseFloat(newValue) || 0)}
              />
              <span className="ml-2 mt-2">pounds</span>
            </div>

            {/* CTA */}
            <div className="flex justify-center flex-col items-center mt-2">
              <Button
                variant="primary"
                label={isLoading ? <InlineSpinner /> : 'Continue'}
                isDisabled={isLoading}
                onClick={() => {
                  if (dogName === '') {
                    toast.error('Please enter dog name', BOTTOM_TOAST);
                  } else if (dogName && !dogNamePattern.test(dogName)) {
                    toast.error(
                      'Dog names may only contain uppercase and lowercase letters',
                      BOTTOM_TOAST
                    );
                  } else if (!weight || weight.toString() === '0' || weight.toString() === '') {
                    toast.error('Please enter dog weight', BOTTOM_TOAST);
                  } else if (weight.toString().includes('.')) {
                    toast.error('Dog weight must be an integer', BOTTOM_TOAST);
                  } else {
                    setIsLoading(true);
                    const planOptionsParams = new URLSearchParams({
                      weight: weight.toString(),
                      birth_year: selectedYear.value,
                      body_type: selectedBodyType.value,
                      neutered: isNeuteredOrSpayed.value,
                      lifestyle: selectedLifestyle.value,
                      account_id: customer.id,
                    });
                    if (selectedMonth !== undefined) {
                      planOptionsParams.set('birth_month', selectedMonth.value);
                    }
                    if (petplan && petplan.flex_frequency_selection !== null) {
                      planOptionsParams.set(
                        'frequency',
                        petplan.flex_frequency_selection.toString()
                      );
                    }
                    axios
                      .get(`calculator/plan_options?${planOptionsParams.toString()}`, {
                        headers: { 'Site-Country': customer.default_country },
                      })
                      .then((planOptionsResponse) => {
                        const buildPlanPayload = buildBuildPlanPayload({
                          planSizeId: planOptionsResponse.data.plan_size.id,
                          recipes: mealPlanProduct?.recipes ?? [],
                          accountId: customer.id,
                          frequency: petplan?.flex_frequency_selection,
                        });
                        axios
                          .post(`calculator/build_plan`, buildPlanPayload, {
                            headers: { 'Site-Country': customer.default_country },
                          })
                          .then((buildPlanResponse) => {
                            setIsLoading(false);
                            setRecommendedMealPlan(buildPlanResponse.data);
                            setStep(UpdatePetStepper.Review);
                          })
                          .catch((e) => {
                            setIsLoading(false);
                            toast.error(generateErrorMsg(e), BOTTOM_TOAST);
                          });
                      })
                      .catch((e) => {
                        setIsLoading(false);
                        toast.error(generateErrorMsg(e), BOTTOM_TOAST);
                      });
                  }
                }}
              />
            </div>
          </div>
        )}
        {step === UpdatePetStepper.Review && (
          <div className="flex justify-left flex-col items-left h-600">
            <div className="text-md text-left">
              <h3>
                <b>Dog name</b>: {dogName}
              </h3>
              <h3 className="mt-2">
                <b>Dog Properties</b>
              </h3>
              <Table
                header={['', 'Previous', 'New']}
                data={[
                  {
                    '': 'Gender',
                    Previous: `${previousGenderOption?.label}`,
                    New: `${selectedGender?.label}`,
                  },
                  {
                    '': 'Neutered/Spayed?',
                    Previous: `${previousNeuteredOrSpayedOption?.label}`,
                    New: `${isNeuteredOrSpayed?.label}`,
                  },
                  {
                    '': 'Birth Month + Year',
                    Previous:
                      previousMonthOption !== undefined
                        ? `${previousMonthOption?.label} ${previousYearOption?.label}`
                        : `${previousYearOption?.label}`,
                    New:
                      selectedMonth !== undefined
                        ? `${selectedMonth?.label} ${selectedYear?.label}`
                        : `${selectedYear?.label}`,
                  },
                  {
                    '': 'Health Conditions',
                    Previous: `${previousHealthConditions.map((cond) => cond.label).join(', ')}`,
                    New: `${selectedHealthConditions.map((cond) => cond.label).join(', ')}`,
                  },
                  {
                    '': 'Body Type',
                    Previous: `${previousBodyTypeOption?.label}`,
                    New: `${selectedBodyType?.label}`,
                  },
                  {
                    '': 'Weight',
                    Previous: `${previousWeight}`,
                    New: `${weight}`,
                  },
                  {
                    '': 'Lifestyle',
                    Previous: `${previousLifestyleOption?.label}`,
                    New: `${selectedLifestyle?.label}`,
                  },
                ]}
              />
              <h3 className="mt-2">
                <b>Meal Plan</b>
              </h3>
              <Table
                header={['', 'Current', 'Recommended']}
                data={[
                  {
                    '': 'Price',
                    Current: `$${mealPlanProduct?.unit_price} ${customer.default_currency}`,
                    Recommended: `$${recommendedMealPlan?.subscription?.price?.price} ${customer.default_currency}`,
                  },
                  {
                    '': 'Code',
                    Current: `${mealPlanProduct?.code}`,
                    Recommended: `${recommendedMealPlan?.subscription?.product?.code}`,
                  },
                  {
                    '': 'Description',
                    Current: (
                      <div className="max-w-[175px] justify-self-center">
                        {mealPlanProduct?.name}
                      </div>
                    ),
                    Recommended: (
                      <div className="max-w-[175px] justify-self-center">
                        {recommendedMealPlan?.subscription?.product?.name}
                      </div>
                    ),
                  },
                ]}
              />
            </div>
            <div className="flex justify-center items-center space-x-4 mt-5">
              <Button
                variant="secondary"
                label="Keep Current Plan"
                onClick={() => setShowCurrentPlanConfirmModal(true)}
              />
              <Button
                variant="primary"
                label="Update to Recommended Plan"
                onClick={() => setShowRecommendedPlanConfirmModal(true)}
              />
            </div>
          </div>
        )}
      </div>
      {showCurrentPlanConfirmModal && (
        <ConfirmationModal
          title="Are you sure you want to proceed?"
          message={`By clicking "Confirm" below, changes to ${dogName}'s profile will be saved but ${possessiveAdj} meal plan will remain the same.`}
          confirmLabel="Confirm"
          onConfirm={onKeepCurrentPlan}
          onCancel={() => setShowCurrentPlanConfirmModal(false)}
        />
      )}
      {showRecommendedPlanConfirmModal && (
        <ConfirmationModal
          title="Are you sure you want to proceed?"
          message={`By clicking "Confirm" below, changes to ${dogName}'s profile will be saved and ${possessiveAdj} meal plan will be updated to match the new recommended plan.`}
          confirmLabel="Confirm"
          onConfirm={onUpdateToRecommendedPlan}
          onCancel={() => setShowRecommendedPlanConfirmModal(false)}
        />
      )}
    </div>
  );
};
